import React, { useState } from 'react';

const TravelPage = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedRegion, setSelectedRegion] = useState(null);

  // Travel statistics from your notes
  const travelStats = {
    countries: 24,
    usStates: 28,
    oceans: 2,
    seas: 5,
    furthestPoints: {
      north: "Sudbury, Canada",
      east: "Castel Campagano, Italy", 
      west: "Hawaii, USA",
      south: "Medellin, Colombia"
    }
  };

  // Organized travel data
  const regions = {
    northAmerica: {
      name: "North America",
      countries: ["USA", "Canada", "Mexico"],
      highlights: ["Grand Canyon", "Hoover Dam", "Pearl Harbor", "Niagara Falls region"]
    },
    caribbean: {
      name: "Caribbean",
      countries: ["Bahamas", "Aruba", "Barbados", "Virgin Islands (US)", "St Maartin", "St Martin", "Curacao", "Dominica", "Dominican Republic", "Haiti", "Turks & Caicos"],
      highlights: ["Island hopping adventures", "Crystal clear waters", "Tropical paradise"]
    },
    southAmerica: {
      name: "South America", 
      countries: ["Colombia"],
      highlights: ["Medellin", "Cultural immersion", "Coffee region"]
    },
    europe: {
      name: "Europe",
      countries: ["Spain", "France", "Italy", "Switzerland", "Belgium", "Netherlands", "Germany", "Czech Republic", "Vatican City"],
      highlights: ["Castel Campagano, Italy", "European heritage tour", "Cultural exploration"]
    }
  };

  const usHighlights = {
    west: {
      name: "Western USA",
      states: ["Arizona", "California", "Colorado", "Hawaii", "Nevada", "Utah"],
      highlights: ["Grand Canyon", "Hoover Dam", "Pearl Harbor", "Aspen", "Las Vegas", "Zion National Park", "Arches", "Bryson Canyon"]
    },
    east: {
      name: "Eastern USA", 
      states: ["Connecticut", "Florida", "Maine", "Massachusetts", "New Hampshire", "New Jersey", "New York", "Pennsylvania", "Rhode Island"],
      highlights: ["Home base Connecticut", "Block Island", "Cape Cod", "NYC", "Boston", "Bar Harbor"]
    },
    midwest: {
      name: "Midwest USA",
      states: ["Illinois", "Indiana", "Michigan", "Ohio"],
      highlights: ["Chicago", "Lake Michigan shores", "Ohio State University"]
    },
    south: {
      name: "Southern USA",
      states: ["Alabama", "Arkansas", "Florida", "Georgia", "Louisiana", "Tennessee", "Texas"],
      highlights: ["New Orleans", "Miami", "Fort Lauderdale", "Houston", "Dallas"]
    }
  };

  return (
    <div className="page-container-wrapper max-width-default padding-default">
      <div className="page-header">
        <h1>
          <i className="fas fa-globe-americas"></i>
          Travel Adventures
        </h1>
        <p>Exploring the world one destination at a time - from my home in Connecticut to the far corners of the globe.</p>
      </div>

      <div className="page-content">
        {/* Travel Statistics */}
        <div className="section">
          <div className="travel-stats">
            <h2>Travel by the Numbers</h2>
            <div className="stats-grid">
              <div className="stat-item">
                <div className="stat-number">{travelStats.countries}</div>
                <div className="stat-label">Countries Visited</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">{travelStats.usStates}</div>
                <div className="stat-label">US States</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">{travelStats.oceans}</div>
                <div className="stat-label">Oceans</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">{travelStats.seas}</div>
                <div className="stat-label">Seas</div>
              </div>
            </div>
          </div>
        </div>

        {/* Furthest Points */}
        <div className="section">
          <div className="furthest-points">
            <h2>My Furthest Points on Earth</h2>
            <div className="compass-grid">
              <div className="compass-point north">
                <i className="fas fa-arrow-up"></i>
                <h4>Furthest North</h4>
                <p>{travelStats.furthestPoints.north}</p>
              </div>
              <div className="compass-point east">
                <i className="fas fa-arrow-right"></i>
                <h4>Furthest East</h4>
                <p>{travelStats.furthestPoints.east}</p>
              </div>
              <div className="compass-point south">
                <i className="fas fa-arrow-down"></i>
                <h4>Furthest South</h4>
                <p>{travelStats.furthestPoints.south}</p>
              </div>
              <div className="compass-point west">
                <i className="fas fa-arrow-left"></i>
                <h4>Furthest West</h4>
                <p>{travelStats.furthestPoints.west}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="section">
          <div className="travel-tabs">
            <button 
              className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              <i className="fas fa-globe"></i>
              World Overview
            </button>
            <button 
              className={`tab-button ${activeTab === 'international' ? 'active' : ''}`}
              onClick={() => setActiveTab('international')}
            >
              <i className="fas fa-passport"></i>
              International
            </button>
            <button 
              className={`tab-button ${activeTab === 'usa' ? 'active' : ''}`}
              onClick={() => setActiveTab('usa')}
            >
              <i className="fas fa-flag-usa"></i>
              United States
            </button>
            <button 
              className={`tab-button ${activeTab === 'oceans' ? 'active' : ''}`}
              onClick={() => setActiveTab('oceans')}
            >
              <i className="fas fa-water"></i>
              Oceans & Seas
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="tab-content">
              <h3>My Travel Journey</h3>
              <p className="travel-intro">
                From my home base in Connecticut, I've been fortunate to explore {travelStats.countries} countries 
                across 4 continents, all {travelStats.usStates} US states, and sail across {travelStats.oceans} oceans 
                and {travelStats.seas} different seas. Each destination has left its mark, creating a tapestry of 
                memories, experiences, and connections that span the globe.
              </p>
              
              <div className="journey-highlights">
                <div className="highlight-card">
                  <i className="fas fa-mountain"></i>
                  <h4>Natural Wonders</h4>
                  <p>From the Grand Canyon to Zion National Park, I've witnessed some of nature's most spectacular creations.</p>
                </div>
                
                <div className="highlight-card">
                  <i className="fas fa-city"></i>
                  <h4>Urban Adventures</h4>
                  <p>Explored vibrant cities from New York to Medellin, each with its own unique character and culture.</p>
                </div>
                
                <div className="highlight-card">
                  <i className="fas fa-umbrella-beach"></i>
                  <h4>Island Paradise</h4>
                  <p>Island-hopped through the Caribbean, discovering pristine beaches and crystal-clear waters.</p>
                </div>
                
                <div className="highlight-card">
                  <i className="fas fa-landmark"></i>
                  <h4>Cultural Heritage</h4>
                  <p>Connected with my Italian roots and explored European history and architecture.</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'international' && (
            <div className="tab-content">
              <h3>International Destinations</h3>
              <div className="regions-grid">
                {Object.entries(regions).map(([key, region]) => (
                  <div key={key} className="region-card">
                    <h4>{region.name}</h4>
                    <div className="countries-list">
                      {region.countries.map((country, index) => (
                        <span key={index} className="country-tag">{country}</span>
                      ))}
                    </div>
                    <div className="region-highlights">
                      <h5>Highlights:</h5>
                      <ul>
                        {region.highlights.map((highlight, index) => (
                          <li key={index}>{highlight}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'usa' && (
            <div className="tab-content">
              <h3>United States Adventures</h3>
              <p>I've been to {travelStats.usStates} out of 50 US states, exploring diverse landscapes from coast to coast.</p>
              
              <div className="us-regions-grid">
                {Object.entries(usHighlights).map(([key, region]) => (
                  <div key={key} className="us-region-card">
                    <h4>{region.name}</h4>
                    <div className="states-count">{region.states.length} states visited</div>
                    <div className="region-highlights">
                      <h5>Notable Destinations:</h5>
                      <ul>
                        {region.highlights.map((highlight, index) => (
                          <li key={index}>{highlight}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'oceans' && (
            <div className="tab-content">
              <h3>Oceans & Seas Explored</h3>
              
              <div className="waters-section">
                <div className="water-category">
                  <h4><i className="fas fa-water"></i> Oceans (2)</h4>
                  <div className="water-list">
                    <div className="water-item">
                      <h5>North Atlantic Ocean</h5>
                      <p>Crossed during European travels and Caribbean adventures</p>
                    </div>
                    <div className="water-item">
                      <h5>North Pacific Ocean</h5>
                      <p>Experienced in Hawaii and along the California coast</p>
                    </div>
                  </div>
                </div>
                
                <div className="water-category">
                  <h4><i className="fas fa-waves"></i> Seas (5)</h4>
                  <div className="water-list">
                    <div className="water-item">
                      <h5>Gulf of Mexico</h5>
                      <p>Florida and Texas coastal experiences</p>
                    </div>
                    <div className="water-item">
                      <h5>Caribbean Sea</h5>
                      <p>Island hopping through multiple Caribbean nations</p>
                    </div>
                    <div className="water-item">
                      <h5>Mediterranean Sea</h5>
                      <p>European coastal adventures</p>
                    </div>
                    <div className="water-item">
                      <h5>Tyrrhenian Sea</h5>
                      <p>Italian coastal exploration</p>
                    </div>
                    <div className="water-item">
                      <h5>Balearic Sea</h5>
                      <p>Spanish Mediterranean coast</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TravelPage;
